import {
  DEFAULT_BRANDING_ACCENT_COLOR,
  DEFAULT_BRANDING_PRIMARY_COLOR,
} from '../model/system-general-settings.model.js';

function normalizeText(value) {
  return value === null || value === undefined ? '' : String(value).trim();
}

function normalizeRegions(values) {
  if (!Array.isArray(values)) {
    return [];
  }

  return [...new Set(values
    .map((value) => String(value || '').trim())
    .filter(Boolean))];
}

function normalizeHexColor(value, fallback) {
  const color = normalizeText(value);
  return /^#[0-9A-Fa-f]{6}$/.test(color) ? color : fallback;
}

function nullableHexColor(value) {
  const color = normalizeText(value);
  return color || null;
}

export function mapSystemGeneralSettingsResponseToDto(payload) {
  const allowedStorageRegions = normalizeRegions(payload?.allowedStorageRegions);
  const primaryColor = normalizeHexColor(payload?.primaryColor, DEFAULT_BRANDING_PRIMARY_COLOR);

  return {
    complianceMode: normalizeText(payload?.complianceMode) || 'gdpr',
    dpoName: normalizeText(payload?.dpoName),
    dpoEmail: normalizeText(payload?.dpoEmail),
    dpoPhone: normalizeText(payload?.dpoPhone),
    dataCenterCountry: normalizeText(payload?.dataCenterCountry),
    dataCenterName: normalizeText(payload?.dataCenterName),
    dataCenterAddress: normalizeText(payload?.dataCenterAddress),
    allowedStorageRegionsText: allowedStorageRegions.join('\n'),
    appName: normalizeText(payload?.appName),
    logoUrl: normalizeText(payload?.logoUrl),
    logoObjectKey: normalizeText(payload?.logoObjectKey),
    primaryColor,
    accentColor: normalizeHexColor(payload?.accentColor, DEFAULT_BRANDING_ACCENT_COLOR),
  };
}

export function buildSystemGeneralSettingsPayloadFromForm(form) {
  const allowedStorageRegions = [...new Set(String(form?.allowedStorageRegionsText || '')
    .split(/\r?\n|,/)
    .map((value) => value.trim())
    .filter(Boolean))];

  return {
    complianceMode: normalizeText(form?.complianceMode) || 'gdpr',
    dpoName: normalizeText(form?.dpoName) || null,
    dpoEmail: normalizeText(form?.dpoEmail) || null,
    dpoPhone: normalizeText(form?.dpoPhone) || null,
    dataCenterCountry: normalizeText(form?.dataCenterCountry) || null,
    dataCenterName: normalizeText(form?.dataCenterName) || null,
    dataCenterAddress: normalizeText(form?.dataCenterAddress) || null,
    allowedStorageRegions,
    appName: normalizeText(form?.appName) || null,
    primaryColor: nullableHexColor(form?.primaryColor),
    accentColor: nullableHexColor(form?.accentColor),
  };
}

export function validateSystemGeneralSettingsPayload(payload) {
  const errors = {};

  if (!['gdpr', 'financial_regulation'].includes(payload.complianceMode)) {
    errors.complianceMode = 'Choose a valid compliance mode.';
  }

  if (payload.dpoEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.dpoEmail)) {
    errors.dpoEmail = 'Enter a valid email address.';
  }

  ['primaryColor', 'accentColor'].forEach((key) => {
    if (payload[key] && !/^#[0-9A-Fa-f]{6}$/.test(payload[key])) {
      errors[key] = 'Enter a valid hex color (#RRGGBB).';
    }
  });

  return errors;
}

export function mapSystemGeneralSettingsFieldErrors(requestError) {
  const errors = {};
  const details = Array.isArray(requestError?.errors) ? requestError.errors : [];

  details.forEach((item) => {
    const parameter = String(item?.parameter || '').trim();
    if ([
      'complianceMode',
      'dpoName',
      'dpoEmail',
      'dpoPhone',
      'dataCenterCountry',
      'dataCenterName',
      'dataCenterAddress',
      'allowedStorageRegions',
      'appName',
      'primaryColor',
      'accentColor',
      'logo',
    ].includes(parameter)) {
      const targetKey = parameter === 'allowedStorageRegions' ? 'allowedStorageRegionsText' : parameter;
      errors[targetKey] = String(item?.message || 'Invalid value').trim() || 'Invalid value';
    }
  });

  return errors;
}
